import { createStore } from 'vuex'
import * as mutationTypes from './mutation-types'
import { getJSONValue, setJSONValue } from '@app/utils/local-storage'
import I18n from 'i18n-js'

// update every hour
const UPDATE_INTERVAL_MS = 1000 * 60 * 60
const changelogApiUrl = new URL('https://api.getbeamer.com/v0/posts')
const changelogWebBaseUrl = 'https://changes.silverfin.com'
const supportedSegments = ['uk', 'be', 'nl', 'lux']
const supportedLocalesBySegment = {
  '': ['en'],
  uk: ['en'],
  be: ['en', 'fr', 'nl'],
  nl: ['en', 'nl'],
  lux: ['en', 'fr'],
}

const getBeamerPosts = async (apiKey, segment) => {
  const requestOptions = {
    method: 'GET',
    headers: new Headers({ 'Beamer-Api-Key': apiKey }),
  }

  const requestParameters = new URLSearchParams({
    filter: segment,
    published: true,
    archived: false,
    expired: false,
  })

  const requestURL = new URL(`?${requestParameters}`, changelogApiUrl)

  return fetch(requestURL, requestOptions).then((response) => response.json())
}

export const mutations = {
  [mutationTypes.MARK_AS_READ](state) {
    state.hasNewChangelog = false
    setJSONValue('hasNewChangelog', false)
  },
  [mutationTypes.SET_NEW_CHANGELOG](state, newChangelog) {
    state.hasNewChangelog = true
    state.latestChangelog = newChangelog.title
    state.lastUpdated = new Date().getTime()
    state.changelogCategory = newChangelog.category
    state.changelogLocale = newChangelog.locale
    state.changelogSegment = newChangelog.segment
    setJSONValue('hasNewChangelog', true)
    setJSONValue('latestChangelog', newChangelog.title)
    setJSONValue('changelogLastUpdated', state.lastUpdated)
    setJSONValue('changelogCategory', newChangelog.category)
    setJSONValue('changelogLocale', newChangelog.locale)
    setJSONValue('changelogSegment', newChangelog.segment)
  },
  [mutationTypes.UPDATE_PERFORMED](state) {
    state.lastUpdated = new Date().getTime()
    setJSONValue('changelogLastUpdated', state.lastUpdated)
  },
}

const actions = {
  markAsRead({ commit }) {
    commit(mutationTypes.MARK_AS_READ)
  },

  updateChangelog({ commit, getters }) {
    if (!getters.updateNeeded) return

    getBeamerPosts(getters.apiKey, getters.segment).then((collection) => {
      const lastPost = collection[0]

      let postTranslation = lastPost.translations.find((translation) => {
        return translation.language === getters.locale.toUpperCase()
      })

      if (!postTranslation) {
        postTranslation = lastPost.translations.find((translation) => {
          return translation.language === I18n.defaultLocale.toUpperCase()
        })
      }

      commit(mutationTypes.SET_NEW_CHANGELOG, {
        title: postTranslation.title,
        category: postTranslation.category,
        locale: getters.locale,
        segment: getters.segment,
      })

      commit(mutationTypes.UPDATE_PERFORMED)
    })
  },
}

export const getters = {
  pulseUser: () => {
    Silverfin.isCurrentUserPulse()
  },

  updateNeeded: (state, getters) => {
    if (!state.lastUpdated) {
      return true
    }

    if (getters.locale !== state.changelogLocale) {
      return true
    }

    if (getters.segment !== state.changelogSegment) {
      return true
    }

    if (!state.latestChangelog) {
      return true
    }

    if (!state.changelogCategory) {
      return true
    }

    if (state.lastUpdated < new Date().getTime() - UPDATE_INTERVAL_MS) {
      return true
    }

    return false
  },

  locale: (_, getters) => {
    let locale = I18n.locale

    if (!supportedLocalesBySegment[getters.segment].includes(locale)) {
      locale = 'en'
    }

    return locale
  },

  changelogUrl: (_, getters) => {
    let queryString = ''

    if (getters.segment) {
      queryString = `?role=${getters.segment}`
    }

    return new URL(`${changelogWebBaseUrl}/${getters.locale}${queryString}`)
  },
}

export default function BeamerBridge(apiKey, segment) {
  return createStore({
    state: {
      lastUpdated: getJSONValue('changelogLastUpdated'),
      hasNewChangelog: getJSONValue('hasNewChangelog') || false,
      latestChangelog: getJSONValue('latestChangelog') || '',
      changelogCategory: getJSONValue('changelogCategory') || '',
      changelogLocale: getJSONValue('changelogLocale') || '',
      changelogSegment: getJSONValue('changelogSegment') || '',
    },
    getters: {
      ...getters,
      apiKey: () => apiKey,
      segment: () => {
        if (segment === 'lu') return 'lux'
        if (segment === 'gb') return 'uk'
        if (!supportedSegments.includes(segment)) return ''

        return segment
      },
    },
    mutations,
    actions,
  })
}
